import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageHeroDecor from '@/components/feature/PageHeroDecor';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useSEO } from '@/hooks/useSEO';

interface FaqItem {
  q: string;
  a: string;
}

interface FaqCategory {
  id: string;
  en: string;
  label: string;
  items: FaqItem[];
}

const faqCategories: FaqCategory[] = [
  {
    id: 'about',
    en: 'About',
    label: 'サービスについて',
    items: [
      {
        q: 'メセナインターコム株式会社はどのような会社ですか？',
        a: '医療・福祉・教育・地域・文化の領域を中心に、意味設計・価値創造・市場創造に取り組む会社です。組織が持つ専門性や誠実な実践を「社会の言葉」に翻訳し、伝わる構造をつくることを大切にしています。単なる制作会社ではなく、組織の意味を掘り起こし、言語化し、循環させるパートナーとして機能します。',
      },
      {
        q: '「意味設計」とは何ですか？',
        a: '組織が「なぜ存在するのか」「何を大切にしているのか」という根本的な問いに向き合い、それを言語化・構造化するプロセスです。多くの組織は手段（SNS・パンフレット・動画）から考えますが、私たちはまず意味を整理します。意味が明確になると、発信の一貫性が生まれ、スタッフが自ら語れるようになり、関わる人が自然と広めてくれる循環が生まれます。',
      },
      {
        q: '制作会社やコンサルティング会社との違いは何ですか？',
        a: '制作会社は「依頼された制作物をつくる」ことを目的とし、手段（媒体・デザイン）から考えます。コンサルティング会社は戦略を提示しますが、言葉や表現の実装まで担うことは少ない。私たちは「意味の言語化から始め、表現の実装まで一貫して担う」ことを原則とします。戦略と制作の間にある「翻訳」の仕事が、私たちの核心です。',
      },
      {
        q: '「価値が伝わっていない」とはどういう状態ですか？',
        a: '例えば、こんな状態です。「スタッフは一生懸命なのに、患者・利用者・保護者に伝わっていない」「パンフレットをつくったが、読まれていない気がする」「SNSを更新しているが、反応がない」「採用に苦労しているが、何が問題かわからない」。これらはすべて、「意味が言語化されていない」ことから生じる症状です。',
      },
      {
        q: 'どのような規模の組織に対応していますか？',
        a: 'スタッフ数名の小規模クリニックから、複数拠点を持つ医療法人・社会福祉法人まで、幅広い規模に対応しています。規模よりも「意味を伝えたい」という意志があるかどうかを重視しています。まずは30分の無料相談で、現状と課題を一緒に整理しましょう。',
      },
    ],
  },
  {
    id: 'process',
    en: 'Process',
    label: 'プロセス・進め方',
    items: [
      {
        q: 'プロジェクトはどのように進みますか？',
        a: 'まず「意味の発掘」から始まります。インタビューやワークショップを通じて、組織の根本的な価値観・強み・独自性を言語化します。次に「構造化」として、発掘した意味を整理し、伝わる言葉と構造に変換します。最後に「制作・実装」として、パンフレット・Webサイト・映像・研修プログラムなど、最適な媒体で表現します。どのフェーズから始めるかは、組織の状況に合わせて柔軟に設計します。',
      },
      {
        q: '最初のインタビューでは何を聞かれますか？',
        a: '「なぜこの仕事を始めたのか」「どんな瞬間にやりがいを感じるか」「利用者・患者・生徒にどうなってほしいか」「組織の中で大切にしていることは何か」といった問いを中心に、1〜2時間かけてお話を伺います。答えが出なくても構いません。問いに向き合うプロセス自体が、意味を掘り起こす作業になります。',
      },
      {
        q: 'プロジェクトの期間はどのくらいですか？',
        a: 'プロジェクトの内容によって異なります。意味設計のみであれば1〜2ヶ月、制作物を含む場合は3〜6ヶ月が目安です。ただし、組織の状況や意思決定のスピードによって変わります。「急いでつくる」より「正しくつくる」を優先するため、無理なスケジュールはご提案しません。',
      },
      {
        q: 'オンラインでの対応は可能ですか？',
        a: 'はい、全国どこからでもオンラインで対応可能です。インタビューやワークショップもオンラインで実施しています。ただし、現場の空気感や文化を直接感じることが重要な場合は、訪問させていただくこともあります。その際の交通費等は別途ご相談します。',
      },
      {
        q: 'プロジェクト終了後のサポートはありますか？',
        a: '「納品で終わり」にしないことが私たちの方針です。制作物の納品後も、スタッフが自ら語れるようになるための研修や、定期的な振り返りセッションを提供しています。また、組織の変化に合わせてコンテンツを更新するサポートも行っています。長期的なパートナーシップを希望される場合は、継続契約もご用意しています。',
      },
      {
        q: '途中で方向性が変わった場合はどうなりますか？',
        a: '意味設計のプロセスでは、当初の想定と異なる「本質」が見えてくることがあります。それは失敗ではなく、むしろ正しく掘り起こせているサインです。方向性の変化は柔軟に対応します。ただし、大幅なスコープ変更が生じる場合は、改めてご相談の上、プロジェクト内容を再設計します。',
      },
    ],
  },
  {
    id: 'scope',
    en: 'Scope',
    label: '対応領域・制作物',
    items: [
      {
        q: 'どのような領域に対応していますか？',
        a: '医療・福祉・教育・地域・文化の5領域を中心に活動しています。具体的には、病院・クリニック・介護施設・障害福祉事業所・学校・NPO・地域団体・文化施設などです。これらの領域に共通するのは「専門性が高く、価値が伝わりにくい」という課題です。私たちはこの課題と長年向き合ってきました。',
      },
      {
        q: 'どのような制作物に対応していますか？',
        a: 'パンフレット・リーフレット・年次報告書・Webサイト・映像・写真・サイン計画・研修プログラム・採用ブランディングなど、幅広い媒体に対応しています。ただし、媒体の選択は意味設計の後に行います。「何をつくるか」より「何を伝えるか」が先です。媒体ありきのご相談でも、まず意味から整理することをご提案します。',
      },
      {
        q: '採用に関する支援も行っていますか？',
        a: 'はい、採用ブランディングは重要な支援領域のひとつです。「なぜここで働くのか」という問いに答える言葉と構造をつくることで、価値観の合う人材が集まりやすくなります。採用パンフレット・採用サイト・スタッフインタビュー映像などを通じて、組織の文化を可視化します。「人が集まらない」という課題の多くは、意味が伝わっていないことに起因しています。',
      },
      {
        q: '写真・映像の撮影も対応していますか？',
        a: 'はい、対応しています。ただし、私たちが重視するのは「何を撮るか」より「何を伝えるか」です。撮影前に意味設計を行い、「この写真・映像で何を感じてほしいか」を明確にした上で撮影に臨みます。そのため、単なる記録撮影ではなく、組織の意味を可視化する撮影になります。',
      },
      {
        q: 'Webサイトのみの制作依頼は受けていますか？',
        a: '受けています。ただし、Webサイトは「意味を伝える器」であるため、コンテンツ（言葉・写真・構成）の設計なしに制作だけを行うことはお勧めしていません。既存のコンテンツがある場合は、それを活かしながら制作します。コンテンツから一緒に考えたい場合は、意味設計とセットでのご提案になります。',
      },
    ],
  },
  {
    id: 'cost',
    en: 'Cost',
    label: '費用・契約',
    items: [
      {
        q: '費用の目安を教えてください。',
        a: 'プロジェクトの内容・規模・期間によって大きく異なるため、一律の料金表はありません。意味設計のみのプロジェクトから、制作物を含む包括的な支援まで、組織の状況に合わせてご提案します。まずは無料相談で現状を共有いただき、その後に見積もりをお出しします。「予算がないから相談できない」ということはありません。予算に合わせた進め方を一緒に考えます。',
      },
      {
        q: '無料相談では何を話しますか？',
        a: '30分の無料相談では、「今、何が散らばっているか」「どこを一旦の完成形とするか」「何から着手すべきか」を一緒に整理します。課題が明確でなくても構いません。「何かが変わらなければならない」という感覚があれば、それが出発点になります。相談後に必ず契約が発生するわけではありませんので、お気軽にどうぞ。',
      },
      {
        q: '契約形態はどのようなものがありますか？',
        a: 'プロジェクト単位の契約と、継続的なパートナーシップ契約の2種類があります。プロジェクト単位は特定の制作物や意味設計に対する契約です。継続契約は、組織の変化に合わせて継続的に支援するもので、月次での定例ミーティング・コンテンツ更新・相談対応などが含まれます。長期的な関係を希望される組織に適しています。',
      },
      {
        q: '分割払いや後払いは可能ですか？',
        a: 'プロジェクトの規模によっては、着手金・中間金・完了金の3回払いに対応しています。また、継続契約の場合は月次払いが基本です。資金繰りの事情がある場合は、個別にご相談ください。「お金の問題で良い仕事ができない」という状況は、双方にとって望ましくないと考えています。',
      },
      {
        q: '助成金や補助金の活用は可能ですか？',
        a: '一部のプロジェクトでは、中小企業向けの補助金（IT導入補助金・小規模事業者持続化補助金など）や、地域の助成制度、社会福祉法人向けの助成金を活用できる場合があります。ただし、制度の適用可否は個別の状況によりますので、相談の中で一緒に確認しましょう。申請書類の作成サポートも対応可能です。',
      },
    ],
  },
  {
    id: 'other',
    en: 'Other',
    label: 'その他',
    items: [
      {
        q: '他の制作会社と並行して相談することはできますか？',
        a: 'もちろんです。複数の選択肢を比較検討することは自然なことです。ただ、私たちの仕事は「意味設計」から始まるため、他社との比較は制作物の価格や品質だけでなく、「どこから考えるか」という視点でも行っていただけると、より適切な判断ができると思います。比較した上で選んでいただけることが、長期的な関係の出発点になります。',
      },
      {
        q: '守秘義務はありますか？',
        a: 'はい、すべてのプロジェクトにおいて守秘義務契約を締結します。組織の内部情報・戦略・課題などは、プロジェクト外に開示することはありません。事例としてWebサイトや実績紹介に掲載する場合は、必ず事前に内容を確認いただき、承認をいただいた上で公開します。',
      },
      {
        q: '地方の組織でも対応できますか？',
        a: 'はい、全国対応しています。これまでも北海道から九州まで、さまざまな地域の組織と仕事をしてきました。地方の組織こそ、「意味が伝わっていない」という課題を抱えていることが多く、私たちの支援が大きな変化をもたらすことがあります。オンラインを基本としつつ、必要に応じて現地訪問も行います。',
      },
      {
        q: '相談したいが、何を相談すればいいかわからない場合は？',
        a: 'それが最も多いご相談のパターンです。「何かが変わらなければならない気がするが、何から手をつければいいかわからない」という状態こそ、私たちが最も力を発揮できる場面です。まず現状を話していただくだけで構いません。30分の対話の中で、課題の輪郭が見えてきます。',
      },
    ],
  },
];

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function FaqPage() {
  useSEO({
    title: 'よくあるご質問（FAQ） | メセナインターコム株式会社',
    description: 'メセナインターコム株式会社へのよくあるご質問。サービス内容・進め方・費用・対応領域など、医療・福祉・教育・地域・文化の意味設計・価値創造コンサルティングについての疑問にお答えします。',
    keywords: 'よくある質問,FAQ,意味設計,コンサルティング,費用,進め方,医療,福祉,教育,地域,文化',
    ogTitle: 'よくあるご質問（FAQ） | メセナインターコム株式会社',
    ogDescription: 'サービス内容・進め方・費用・対応領域など、メセナインターコム株式会社へのよくあるご質問をまとめました。',
    ogUrl: 'https://mecena-intercom.co.jp/faq',
    canonical: 'https://mecena-intercom.co.jp/faq',
  });

  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [activeCategory, setActiveCategory] = useState<string>('about');
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, []);

  const toggleItem = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const activeData = faqCategories.find((c) => c.id === activeCategory);

  return (
    <div className="bg-warm-white min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-20 md:pt-52 md:pb-28 bg-warm-white relative overflow-hidden">
          <PageHeroDecor variant="gold" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div
                ref={heroRef}
                className="md:col-span-8 md:col-start-3"
                style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s' }}
              >
                <div className="flex items-center gap-4 mb-10">
                  <span className="block w-6 h-px bg-accent-teal" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">FAQ</span>
                </div>
                <h1 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-4xl md:text-5xl mb-10">
                  よくあるご質問
                </h1>
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp max-w-xl">
                  サービス内容・進め方・費用など、よくいただくご質問をまとめました。<br />
                  ここに載っていない疑問は、お気軽にご相談ください。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Body */}
        <section className="py-16 md:py-24 bg-warm-light">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">

              {/* Category nav */}
              <FadeIn delay={0} className="md:col-span-3">
                <aside>
                  <div className="md:sticky md:top-28">
                    <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-6">Categories</p>
                    <nav className="space-y-0 border-t border-divider">
                      {faqCategories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => setActiveCategory(cat.id)}
                          className={`w-full flex items-center justify-between py-4 border-b border-divider text-left transition-colors duration-200 cursor-pointer group ${activeCategory === cat.id ? 'text-charcoal' : 'text-muted-brown hover:text-charcoal'}`}
                        >
                          <div>
                            <span className={`block font-garamond text-xs italic mb-1 transition-colors duration-200 ${activeCategory === cat.id ? 'text-accent-teal' : 'text-muted-light group-hover:text-accent-teal'}`}>
                              {cat.en}
                            </span>
                            <span className="font-sans-jp text-sm tracking-wide-jp">{cat.label}</span>
                          </div>
                          <span className={`block w-1 h-1 rounded-full flex-shrink-0 transition-colors duration-200 ${activeCategory === cat.id ? 'bg-accent-teal' : 'bg-divider'}`} />
                        </button>
                      ))}
                    </nav>
                  </div>
                </aside>
              </FadeIn>

              {/* FAQ items */}
              <FadeIn delay={0.15} className="md:col-span-9">
                {activeData && (
                  <div>
                    <div className="flex items-center gap-4 mb-10">
                      <span className="font-garamond text-xs italic text-muted-light">{activeData.en}</span>
                      <span className="block flex-1 h-px bg-divider" />
                    </div>
                    <div className="space-y-0 border-t border-divider">
                      {activeData.items.map((item, idx) => {
                        const key = `${activeData.id}-${idx}`;
                        const isOpen = !!openItems[key];
                        return (
                          <div key={key} className="border-b border-divider">
                            <button
                              onClick={() => toggleItem(key)}
                              className="w-full flex items-start gap-6 py-7 text-left cursor-pointer group"
                            >
                              <span className="font-garamond text-xs italic text-muted-light flex-shrink-0 mt-0.5 w-5">
                                Q
                              </span>
                              <span className={`flex-1 font-serif-jp font-light tracking-normal-jp leading-snug text-base transition-colors duration-200 ${isOpen ? 'text-charcoal' : 'text-charcoal group-hover:text-accent-teal'}`}>
                                {item.q}
                              </span>
                              <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center border transition-all duration-300 mt-0.5 ${isOpen ? 'border-accent-teal rotate-45' : 'border-divider group-hover:border-charcoal'}`}>
                                <i className={`ri-add-line text-xs transition-colors duration-200 ${isOpen ? 'text-accent-teal' : 'text-muted-light group-hover:text-charcoal'}`} />
                              </span>
                            </button>
                            <div
                              className={`overflow-hidden transition-all duration-400 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                            >
                              <div className="flex gap-6 pb-8">
                                <span className="font-garamond text-xs italic text-accent-teal flex-shrink-0 mt-0.5 w-5">A</span>
                                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp flex-1">
                                  {item.a}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Still have questions */}
        <section className="py-20 md:py-28 bg-warm-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <FadeIn delay={0} className="md:col-span-8 md:col-start-3">
                <div className="border border-divider p-10 md:p-16">
                  <div className="flex items-center gap-4 mb-8">
                    <span className="block w-6 h-px bg-accent-teal" />
                    <span className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase">Still have questions?</span>
                  </div>
                  <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-2xl md:text-3xl mb-6">
                    疑問が解決しない場合は、<br />直接ご相談ください。
                  </h2>
                  <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp mb-10 max-w-lg">
                    30分の無料相談で、現状の「意味」を一緒に整理します。<br />
                    課題が明確でなくても、「何かが変わらなければならない」という感覚があれば十分です。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 px-8 py-4 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap"
                    >
                      無料相談を申し込む
                    </Link>
                    <Link
                      to="/dekirukoto"
                      className="inline-flex items-center gap-2 px-8 py-4 border border-charcoal text-sm font-sans-jp text-charcoal tracking-wide-jp hover:bg-charcoal hover:text-warm-white transition-all duration-300 whitespace-nowrap"
                    >
                      できることを見る →
                    </Link>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
